import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AlertDialog from '../../../components/ui/AlertDialog';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/Dialog';
import { DialogErrorAlert, useDialogError } from '../../../components/ui/DialogError';
import { renameShiftSet } from '../../../api/Attendance/scheduling';
import { isoDateToDisplay } from '../../../utils/appDateFormat';
import { todayLocalDateParam } from '../../../utils/apiDate';

/**
 * CO-1021, the two questions asked before a schedule save sends anything (owner, 2026-11-03).
 *
 * "I guess we should let them choose a differnt name for either of the two": a name another schedule already has
 * (in any case) opens a dialog with both names, and either can be changed there. Renaming the other schedule goes
 * through its own name-only route; the rule PUT would archive and remake its future rows. The check is the client's:
 * the server has no name rule, and it only sees the schedules this user can see.
 *
 * "Warn, let them save": a past start on a new schedule says what it will write. Continuing saves as before; the
 * server's handling of past days does not change.
 *
 * Ask the past start first and the name last. A rename is the only thing these questions send, so it stays the last
 * step before the save, and going back from the past start leaves everything as it was.
 */
export type SchedulePreSaveRef = { id?: number | string | null; name?: string | null; assignedUserIds?: unknown[] | null };
export type ScheduleNameQuestion = {
  name: string;
  schedules: SchedulePreSaveRef[] | null | undefined;
  /** The schedule being edited. It can keep its own name, and a new schedule has none. */
  selfId?: number | string | null;
};
export type ScheduleNameAnswer = { name: string; renamedOther: { id: number; name: string } | null };

const nameKey = (name: string | null | undefined) => (name || '').trim().toLowerCase();
const sameId = (a: unknown, b: unknown) => a != null && b != null && Number(a) === Number(b);

/** Both are yyyy-MM-dd, so a string compare is a date compare. */
export const isPastStart = (date: string | null | undefined): boolean => !!date && date < todayLocalDateParam();

type NameDialogProps = {
  question: ScheduleNameQuestion;
  other: SchedulePreSaveRef;
  onDone: (answer: ScheduleNameAnswer | null) => void;
};

function ScheduleNameClashDialog({ question, other, onDone }: NameDialogProps) {
  const { t } = useTranslation();
  const dialogError = useDialogError();
  const [thisName, setThisName] = useState(question.name);
  const [otherName, setOtherName] = useState(other.name || '');
  const [busy, setBusy] = useState(false);

  const thisTrimmed = thisName.trim();
  const otherTrimmed = otherName.trim();
  const otherChanged = otherTrimmed !== (other.name || '').trim();
  // Schedules outside this pair. An old duplicate of the other schedule's name does not block keeping that name.
  const takenElsewhere = (name: string) => (question.schedules || []).some((s) =>
    !sameId(s?.id, other.id) && !sameId(s?.id, question.selfId) && nameKey(s?.name) === nameKey(name));

  let problem = '';
  let isSame = false;
  if (!thisTrimmed || !otherTrimmed) problem = t('ScheduleNameClashEmpty');
  else if (nameKey(thisTrimmed) === nameKey(otherTrimmed)) {
    problem = t('ScheduleNameClashStillSame');
    isSame = true;
  } else if (takenElsewhere(thisTrimmed)) problem = t('ScheduleNameClashTaken', { name: thisTrimmed });
  else if (otherChanged && takenElsewhere(otherTrimmed)) problem = t('ScheduleNameClashTaken', { name: otherTrimmed });

  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (problem || busy) return;
    dialogError.clear();
    setBusy(true);
    try {
      if (otherChanged) await renameShiftSet(Number(other.id), otherTrimmed);
      onDone({ name: thisTrimmed, renamedOther: otherChanged ? { id: Number(other.id), name: otherTrimmed } : null });
    } catch (err) {
      // Nothing else was sent: the dialog stays open and says why, and renaming this schedule instead still works.
      dialogError.capture(err);
    } finally {
      setBusy(false);
    }
  };

  const peopleCount = Array.isArray(other.assignedUserIds) ? other.assignedUserIds.length : 0;
  return (
    <Dialog open onOpenChange={(next) => { if (!next && !busy) onDone(null); }}>
      <DialogContent
        hideCloseButton
        data-testid="schedule-name-clash"
        onEscapeKeyDown={(e) => { if (busy) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (busy) e.preventDefault(); }}
        onInteractOutside={(e) => { if (busy) e.preventDefault(); }}
      >
        <form onSubmit={handleContinue} className="contents">
          <DialogHeader><DialogTitle>{t('ScheduleNameClashTitle')}</DialogTitle></DialogHeader>
          <DialogBody className="max-h-[60vh] overflow-y-auto">
            <div className="space-y-4">
              <DialogErrorAlert error={dialogError.error} />
              <DialogDescription>{t('ScheduleNameClashBody')}</DialogDescription>
              <Input
                label={t('ScheduleNameClashThisOne')}
                value={thisName}
                dir="auto"
                data-testid="schedule-name-clash-this"
                disabled={busy}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setThisName(e.target.value)}
              />
              <div className="space-y-1.5">
                <Input
                  label={t('ScheduleNameClashOtherOne', { count: peopleCount })}
                  value={otherName}
                  dir="auto"
                  data-testid="schedule-name-clash-other"
                  disabled={busy}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOtherName(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">{t('ScheduleNameClashOtherKeeps')}</p>
              </div>
              {/* Reserved height, so the buttons do not move when the names come apart. */}
              <p
                role="status"
                data-testid="schedule-name-clash-rule"
                className={`min-h-[1.25rem] text-sm [overflow-wrap:anywhere] ${isSame ? 'text-muted-foreground' : 'text-destructive'}`}
              >
                {problem}
              </p>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" disabled={busy} onClick={() => onDone(null)}>{t('ScheduleSaveGoBack')}</Button>
            <Button type="submit" disabled={!!problem || busy} loading={busy}>{t('ScheduleNameClashContinue')}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

type PastStartQuestion = { date: string; people: number };
type NameClashState = { question: ScheduleNameQuestion; other: SchedulePreSaveRef };

/**
 * `askPastStart` resolves true to continue. `askName` resolves to the name to save and any rename it made, at once
 * when there is no clash, or null when the user went back. Render `dialogs` once in the page, outside its form.
 */
export function useSchedulePreSave() {
  const { t } = useTranslation();
  const [pastStart, setPastStart] = useState<PastStartQuestion | null>(null);
  const [clash, setClash] = useState<NameClashState | null>(null);
  const resolvePastRef = useRef<((go: boolean) => void) | null>(null);
  const resolveNameRef = useRef<((answer: ScheduleNameAnswer | null) => void) | null>(null);
  // A page that goes away while a question is open answers it, so its save does not stay pending.
  useEffect(() => () => {
    resolvePastRef.current?.(false);
    resolveNameRef.current?.(null);
  }, []);

  const settlePast = useCallback((go: boolean) => {
    const resolve = resolvePastRef.current;
    resolvePastRef.current = null;
    setPastStart(null);
    resolve?.(go);
  }, []);

  const askPastStart = useCallback((date: string, people: number) => new Promise<boolean>((resolve) => {
    resolvePastRef.current?.(false);
    resolvePastRef.current = resolve;
    setPastStart({ date, people });
  }), []);

  const settleName = useCallback((answer: ScheduleNameAnswer | null) => {
    const resolve = resolveNameRef.current;
    resolveNameRef.current = null;
    setClash(null);
    resolve?.(answer);
  }, []);

  const askName = useCallback((question: ScheduleNameQuestion) => new Promise<ScheduleNameAnswer | null>((resolve) => {
    const other = (question.schedules || []).find((s) =>
      !sameId(s?.id, question.selfId) && nameKey(s?.name) === nameKey(question.name));
    if (!other) {
      resolve({ name: question.name, renamedOther: null });
      return;
    }
    resolveNameRef.current?.(null);
    resolveNameRef.current = resolve;
    setClash({ question, other });
  }), []);

  const dialogs = (
    <>
      <AlertDialog
        open={!!pastStart}
        onOpenChange={(isOpen) => { if (!isOpen) settlePast(false); }}
        title={t('SchedulePastStartTitle')}
        description={pastStart ? t('SchedulePastStartBody', { date: isoDateToDisplay(pastStart.date), count: pastStart.people }) : ''}
        variant="destructive"
        confirmLabel={t('SchedulePastStartContinue')}
        cancelLabel={t('ScheduleSaveGoBack')}
        // Resolved here, before AlertDialog closes itself with onOpenChange(false), whose settle is then a no-op.
        onConfirm={() => settlePast(true)}
      />
      {clash && <ScheduleNameClashDialog question={clash.question} other={clash.other} onDone={settleName} />}
    </>
  );
  return { askPastStart, askName, dialogs };
}
